import { Component, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { BadgeColour, ComponentsModule, DialogService } from '@mastechnicaegroup/carelinelive-ui';
import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { Activity } from '@lib/api/models/activity';
import { AppDatePipe } from '@shared/pipes/date.pipe';

interface ChangedField {
    key: string;
    label: string;
    oldValue: string;
    newValue: string;
}

@Component({
    selector: 'app-view-client-audit-entry-dialog',
    imports: [
        CommonModule,
        ComponentsModule,
        AppDatePipe,
    ],
    templateUrl: './view-client-audit-entry-dialog.component.html',
    standalone: true,
    styleUrl: './view-client-audit-entry-dialog.component.scss'
})
export class ViewClientAuditEntryDialogComponent {

    protected readonly entry = inject<Activity>(DIALOG_DATA);
    protected readonly dialogRef = inject<DialogRef>(DialogRef);

    protected readonly showProperties = signal(false);

    protected toggleProperties(): void {
        this.showProperties.update(v => !v);
    }

    protected eventSubtitle(event: string | null): string {
        switch (event) {
            case 'created': return 'Client Created';
            case 'updated': return 'Client Updated';
            case 'deleted': return 'Client Deleted';
            default:        return (event ?? 'unknown')
                .split('_')
                .map(w => w.charAt(0).toUpperCase() + w.slice(1))
                .join(' ');
        }
    }

    protected eventBadgeColour(event: string | null): BadgeColour {
        switch (event) {
            case 'created': return 'success';
            case 'updated': return 'primary';
            case 'deleted': return 'error';
            default:        return 'variant';
        }
    }

    protected eventIcon(event: string | null): string {
        switch (event) {
            case 'created': return 'add';
            case 'updated': return 'save';
            case 'deleted': return 'delete';
            default:        return 'info';
        }
    }

    protected eventLabel(event: string | null): string {
        switch (event) {
            case 'created': return 'CREATED';
            case 'updated': return 'UPDATED';
            case 'deleted': return 'DELETED';
            default:        return (event ?? 'UNKNOWN').replace(/_/g, ' ').toUpperCase();
        }
    }

    protected causerDisplay(): string {
        const causer = this.entry.causer;
        if (!causer) {
            return '—';
        }
        return causer.name ? `${causer.name} (${causer.email})` : causer.email;
    }

    protected changedFields(): ChangedField[] {
        const attributes = this.entry.properties?.attributes;
        const old = this.entry.properties?.old ?? {};

        if (!attributes || typeof attributes !== 'object') {
            return [];
        }

        return Object.entries(attributes).map(([key, value]) => ({
            key,
            label: key.replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase()),
            oldValue: this.formatPropertyValue(old[key]),
            newValue: this.formatPropertyValue(value),
        }));
    }

    private formatPropertyValue(value: unknown): string {
        if (value == null) {
            return '—';
        }
        if (typeof value === 'object' && !Array.isArray(value)) {
            const v = value as Record<string, unknown>;
            if ('number' in v && 'type' in v) {
                return `${v['number']} (${v['type']})`;
            }
        }
        if (Array.isArray(value)) {
            return value.map(item => this.formatPropertyValue(item)).join(', ');
        }
        return String(value);
    }

    public static show(dialog: DialogService, data: Activity) {
        return dialog.openDialog<DialogRef, Activity>(ViewClientAuditEntryDialogComponent, {
            data
        });
    }
}
